import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import DepartamentoEmpresaTable from "components/tables/DepartamentoEmpresaTable";
import {
  AlterarDepartamentoDtoType,
  departamentoBreadcrumbItems,
} from "data/departamentos";
import { transformErrors } from "helpers/utils";
import { useEffect } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useToast } from "providers/ToastProvider";

const EditarDepartamento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { modalState, getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();

  const { isLoading, isError, data, error } = useQuery<{
    data: AlterarDepartamentoDtoType;
  }>({
    queryKey: ["departamento", id],
    queryFn: () => axios.get(`/api/departamento/${id}`),
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: ["departamento", id],
      defaultMessage: "Você não possui permissão para visualizar este departamento"
    }),
  });

  const mutation = useMutation<
    any,
    any,
    { id: string; data: AlterarDepartamentoDtoType }
  >({
    mutationFn: ({ id, data }) => axios.put(`/api/departamento/${id}`, data),
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar departamento", 
      (error) => {
        // Tratamento de erros não relacionados à permissão
        const formattedErrors = transformErrors(error.response?.data?.errors || {});
        for (const [field, error] of Object.entries(formattedErrors)) {
          form.setError(field as any, error as any);
        }
      }
    ),
    onSuccess: (response) => {
      if (response.data.isSuccess) {
        const codigoDepartamento = response.data.data?.codigo || departamento?.codigo;

        // Reset the form's dirty state after successful save
        form.reset(form.getValues(), {
          keepValues: true,
          keepDirtyValues: false,
        });

        // Navigate back to departments list with success state
        navigate("/cadastros/departamentos");
        showToast(<>Departamento <strong>{codigoDepartamento}</strong> atualizado com sucesso!</>, "success");
      }
    },
  });

  const departamento = data?.data;

  const form = useForm<AlterarDepartamentoDtoType>();

  useEffect(() => {
    if (departamento) {     
      form.reset(departamento);
    }
  }, [departamento]);

  if (isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (isError && !modalState.isOpen) {
    return <div>Ocorreu um erro ao carregar o departamento</div>;
  }

  if (!departamento && !modalState.isOpen) {
    return <div>Departamento não encontrado</div>;
  }

  const onSubmit: SubmitHandler<AlterarDepartamentoDtoType> = (data) => {
    mutation.mutate({ data, id: (id as string) });
  };

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb
          items={departamentoBreadcrumbItems(departamento?.codigo)}
        />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar um departamento</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações do departamento
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={() => form.reset()}
              >
                Descartar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Informações do departamento</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={2}
                  xl={2}
                  className="mb-5"
                  controlId="codigo"
                >
                  <Form.Control disabled value={departamento?.codigo} required />
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={8}
                  xl={10}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição do departamento"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <div className="mb-5">
                <h4 className="mb-3">Departamento por empresa</h4>
                <DepartamentoEmpresaTable />
              </div>
            </Col>
            <Col xs={12} xl={4}></Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarDepartamento;
